import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Eye, History, ChevronLeft, ChevronRight, Package, MapPin, User, Phone, Calendar, DollarSign, CreditCard, Truck, AlertCircle, CheckCircle, Clock, QrCode, Printer, X, Pencil } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import MainService from '../services/mainservice';
import { getConfig } from '../services/config';
import QRCode from 'qrcode';
import Barcode from 'react-barcode';

const EDITABLE_ORDER_STATUS_KEYWORDS = ['PKS', 'PKD', 'PKR', 'INS', 'RTN', 'INT'];

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusHistory, setStatusHistory] = useState([]);
  const [orderHistory, setOrderHistory] = useState([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyType, setHistoryType] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);
  const { showSuccess, showError, showWarning } = useToast();

  useEffect(() => {
    if (orderId) {
      loadOrderDetails();
    }
  }, [orderId]);

  const loadOrderDetails = async () => {
    try {
      setIsLoading(true);
      
      // Get token from localStorage
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      if (!tokenData) {
        showError('Please login to view order details');
        navigate('/login');
        return;
      }

      const userData = JSON.parse(tokenData);
      const token = userData.Token;

      // Load order details
      const orderResponse = await MainService.OrderByIdInClApp(orderId, token);
      console.log('Order Details Response:', orderResponse);
      setOrder(orderResponse);

      // Generate QR Code
      if (orderResponse?.VoucherNo) {
        const qrUrl = await QRCode.toDataURL(orderResponse.VoucherNo);
        setQrCodeUrl(qrUrl);
      }

    } catch (error) {
      console.error('Error loading order details:', error);
      showError('Failed to load order details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusClick = async () => {
    if (!order?.Id) return;

    try {
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      const userData = JSON.parse(tokenData);
      const token = userData.Token;

      const response = await MainService.OrderStatusByOrderId(order.Id, token);
      setStatusHistory(response || []);
      setHistoryType('Status');
      setShowStatusModal(true);
    } catch (error) {
      console.error('Error loading status history:', error);
      showError('Failed to load status history');
    }
  };

  const handleHistoryClick = async () => {
    if (!order?.Id) return;

    try {
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      const userData = JSON.parse(tokenData);
      const token = userData.Token;

      const response = await MainService.OrderHistoryByOrderId(order.Id, token);
      setOrderHistory(response || []);
      setHistoryType('History');
      setShowHistoryModal(true);
    } catch (error) {
      console.error('Error loading order history:', error);
      showError('Failed to load order history');
    }
  };

  const handleEditClick = () => {
    if (!order?.Id) return;
    const kw = order.Status?.Keyword;
    if (kw && !EDITABLE_ORDER_STATUS_KEYWORDS.includes(kw)) {
      showWarning('Cannot Update Order Because Its In Processing Stage');
      return;
    }
    navigate(`/new-order/edit/${order.Id}`);
  };

  const handlePrint = async () => {
    if (!order?.Id) {
      showError('Order ID not found');
      return;
    }

    try {
      setIsPrinting(true);
      const response = await MainService.PrintOrder(order.Id);
      
      if (response) {
        // Open the PDF in a new tab
        const pdfUrl = `${getConfig().API_BASE_URL}ReportFiles/${response}`;
        window.open(pdfUrl, '_blank');
        showSuccess('PDF generated successfully');
      } else {
        showError('Print Error - No data received');
      }
    } catch (error) {
      console.error('Print Error:', error);
      showError('Print Error - Failed to generate PDF');
    } finally {
      setIsPrinting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return `AED ${parseFloat(amount).toFixed(2)}`;
  };

  const safeString = (value, fallback = 'N/A') => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'object' && value.Name) return value.Name;
    if (typeof value === 'object' && value.Description) return value.Description;
    return fallback;
  };

  if (isLoading) {
    return (
      <div className="page-container bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="page-container bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Order Not Found</h3>
          <p className="text-gray-600 mb-4">The order you're looking for doesn't exist or you don't have permission to view it.</p>
          <button
            onClick={() => navigate('/deliveries')}
            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg transition-colors duration-200"
          >
            Back to Deliveries
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold text-gray-900">Order Details</h1>
            <span className="text-sm text-gray-500">Order {order.VoucherNo || 'N/A'}</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleStatusClick}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              <Eye className="w-4 h-4" />
              <span>Status</span>
            </button>
            <button
              onClick={handleHistoryClick}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              <History className="w-4 h-4" />
              <span>History</span>
            </button>
            <button
              type="button"
              onClick={handleEditClick}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              <Pencil className="w-4 h-4" />
              <span>Edit</span>
            </button>
            <button
              onClick={handlePrint}
              disabled={isPrinting}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPrinting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Printer className="w-4 h-4" />
                  <span>Print</span>
                </>
              )}
            </button>
            <button
              onClick={() => navigate('/deliveries')}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Back to Deliveries</span>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div id="printable-content">
          {/* Top Section - Three Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* QR Code and Barcode Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-green-600 mb-4">{order.VoucherNo || 'N/A'}</h2>
                {qrCodeUrl && (
                  <div className="mb-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 inline-block">
                      <img src={qrCodeUrl} alt="QR Code" className="w-32 h-32 mx-auto" />
                    </div>
                  </div>
                )}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 flex justify-center">
                  <Barcode value={order.VoucherNo || 'N/A'} width={2} height={60} />
                </div>
              </div>
            </div>

            {/* Order Info Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <Package className="w-5 h-5 text-orange-500 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Order Info</h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Truck className="w-4 h-4 text-green-500 mr-2" />
                  <span className="text-green-600 font-semibold">{safeString(order.Status)}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Client Bill No:</span>
                    <span className="font-medium">{order.ClientBillNo || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Order Date:</span>
                    <span className="font-medium">{formatDate(order.Date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pickup Date:</span>
                    <span className="font-medium">{formatDate(order.PickupDate)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Delivery Date:</span>
                    <span className="font-medium">{formatDate(order.DeliveryDate)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Pickup Address Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <MapPin className="w-5 h-5 text-blue-500 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Pickup Address</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-600">Address:</span>
                  <p className="font-medium">{order.ClientAddress?.Address || 'N/A'}</p>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">PO Box:</span>
                  <span className="font-medium">{order.ClientAddress?.POBox || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Location:</span>
                  <span className="font-medium">{order.ClientAddress?.Location.Name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Emirate:</span>
                  <span className="font-medium">{order.ClientAddress?.Emirate.Name}</span>
                </div>
              </div>
                <br></br>
              <div className="flex items-center mb-4">
                <Truck className="w-5 h-5 text-purple-500 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Delivery Address</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-600">Address:</span>
                  <p className="font-medium">{order.CustomerAddress?.Address || 'N/A'}</p>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">PO Box:</span>
                  <span className="font-medium">{order.CustomerAddress?.POBox || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Location:</span>
                  <span className="font-medium">{safeString(order.CustomerAddress?.Location.Name)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Emirate:</span>
                  <span className="font-medium">{safeString(order.CustomerAddress?.Emirate.Name)}</span>
                </div>
              </div>

            </div>
          </div>

          {/* Bottom Section - Four Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Customer Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <User className="w-5 h-5 text-green-500 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Customer</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-medium">{order.Customer?.Name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Mobile 1:</span>
                  <span className="font-medium">{order.Customer?.MobileNo1 || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Mobile 2:</span>
                  <span className="font-medium">{order.Customer?.MobileNo2 || 'N/A'}</span>
                </div>
              </div>
            </div>

          

            {/* Payment Info Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <DollarSign className="w-5 h-5 text-green-500 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Payment Info</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Product Price:</span>
                  <span className="font-medium">{formatCurrency(order.ProductPrice)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Delivery Charge:</span>
                  <span className="font-medium">{formatCurrency(order.DeliveryCharge)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax:</span>
                  <span className="font-medium">{formatCurrency(order.TAX)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Collection Amount:</span>
                  <span className="font-medium">{formatCurrency(order.CustomerAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">My Payment:</span>
                  <span className="text-green-600 font-bold text-lg">{formatCurrency(order.ClientAmount)}</span>
                </div>
              </div>
            </div>

            {/* Delivery Info Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center mb-4">
                <Truck className="w-5 h-5 text-blue-500 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Delivery Info</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Pay Type:</span>
                  <span className="font-medium">{order.PayMode || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Status:</span>
                  <span className="font-medium">{order.PaymentStatus || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Return Status:</span>
                  <span className="font-medium">{order.ReturnStatus || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Attempts:</span>
                  <span className="font-medium">{order.Attempts || '0'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Remarks:</span>
                  <span className="font-medium">{order.Remarks || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Reason:</span>
                  <span className="font-medium">{order.Reason.Name || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Status Modal */}
        {showStatusModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Status History</h3>
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="space-y-4">
                {statusHistory.length > 0 ? (
                  statusHistory.map((status, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-3 h-3 bg-blue-500 rounded-full mt-2"></div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">{status.StatusName || status.Name}</h4>
                          <span className="text-sm text-gray-500">{formatDate(status.CreatedDate || status.Date)}</span>
                        </div>
                        {status.Remarks && (
                          <p className="text-sm text-gray-600 mt-1">{status.Remarks}</p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No status history available</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* History Modal */}
        {showHistoryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Order History</h3>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="space-y-4">
                {orderHistory.length > 0 ? (
                  orderHistory.map((history, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full mt-2"></div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">{history.Action || history.Description}</h4>
                          <span className="text-sm text-gray-500">{formatDate(history.CreatedDate || history.Date)}</span>
                        </div>
                        {history.Notes && (
                          <p className="text-sm text-gray-600 mt-1">{history.Notes}</p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No order history available</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetails;
